import React, { useEffect, useState } from "react";

let cv = null;

const waitForOpenCv = async ({ timeoutMs = 5000, pollMs = 50 } = {}) => {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const globalCv = typeof window !== "undefined" ? window.cv : null;
    const readyFlag = typeof window !== "undefined" ? window.__cvReady : false;

    if ((readyFlag && globalCv) || (globalCv && typeof globalCv.Mat === "function")) {
      cv = globalCv;
      return true;
    }

    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }

  return false;
};

const DETECTION_PARAMS = {
  opencvTimeoutMs: 5000,
  // Conservative performance cap: keep enough spatial detail for subtle
  // parallax while still avoiding full-resolution OpenCV work on mobile.
  analysisMaxDimension: 960,

  // Feature extraction / matching
  minRawMatches: 20,
  maxGoodMatches: 120,
  ransacReprojThreshold: 3.0,

  // Motion thresholds
  minMotionMean: 0.6,
  minResidualMean: 1.0,
  minResidualStdDev: 0.6,
  minRegionMotionDelta: 2.0,  // STEP 13: raised from 0.8 — all-features computation gives screens ~0–3, real scenes 10–50+

  // Decision thresholds
  fakeDepthThreshold: 0.22,
  verifyThreshold: 0.38,

  // Fallback
  fallbackCanvasSize: 96,
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const safeMean = (arr) => {
  if (!arr || arr.length === 0) return 0;
  return arr.reduce((sum, value) => sum + value, 0) / arr.length;
};

const safeStdDev = (arr, mean = safeMean(arr)) => {
  if (!arr || arr.length === 0) return 0;
  const variance = arr.reduce((sum, value) => sum + (value - mean) ** 2, 0) / arr.length;
  return Math.sqrt(variance);
};

const normalize01 = (value, min, max) => {
  if (max <= min) return 0;
  return clamp((value - min) / (max - min), 0, 1);
};

const resizeImageForAnalysis = (img, maxDimension) => {
  const width = img?.naturalWidth || img?.width || 0;
  const height = img?.naturalHeight || img?.height || 0;

  if (!width || !height || Math.max(width, height) <= maxDimension) {
    return img;
  }

  const scale = maxDimension / Math.max(width, height);
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return img;

  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  return canvas;
};

const AnalyzeDepthFromFrames = ({
  frames,
  frame1,
  frame2,
  orientations,
  onAnalysisComplete,
}) => {
  const [localAnalysisData, setLocalAnalysisData] = useState(null);
  const [depthScoreValue, setDepthScoreValue] = useState(null);
  const [depthMode, setDepthMode] = useState(null); // "opencv" | "fallback"
  const [depthError, setDepthError] = useState(null);

  useEffect(() => {
    const resolvedFrames =
      Array.isArray(frames) && frames.length
        ? frames
        : [frame1, frame2].filter(Boolean);

    const resolvedOrientations =
      Array.isArray(orientations) && orientations.length
        ? orientations
        : [null, null];

    if (resolvedFrames?.length === 2) {
      analyzeFrames(resolvedFrames, resolvedOrientations);
    }
  }, [frames, frame1, frame2, orientations]);

  // STEP 12b — torch alert removed: flashlight step is no longer part of the challenge.
  // Gray-zone outcomes are handled silently by the aggregator.

  const analyzeFrames = async (resolvedFrames, resolvedOrientations) => {
    setDepthError(null);

    if (!cv || typeof cv.Mat !== "function") {
      const ok = await waitForOpenCv({ timeoutMs: DETECTION_PARAMS.opencvTimeoutMs });

      if (!ok || !cv || typeof cv.Mat !== "function") {
        return runFallback(resolvedFrames, "OpenCV not ready within timeout.");
      }
    }

    try {
      const result = await processImages(resolvedFrames, resolvedOrientations);
      const depthScore = clamp(Math.round((result.analysisData.depthConsistency ?? 0) * 100), 0, 100);

      setLocalAnalysisData({
        ...result.analysisData,
        outcome: result.outcome,
      });
      setDepthScoreValue(depthScore);
      setDepthMode("opencv");

      if (typeof onAnalysisComplete === "function") {
        const payload = { ...result, depthScore };

        if (onAnalysisComplete.length >= 2) {
          onAnalysisComplete(depthScore, result?.confidence ?? 0);
        } else {
          onAnalysisComplete(payload);
        }
      }
    } catch (error) {
      console.error("❌ Depth analysis failed:", error);
      setDepthError(error?.message ? String(error.message) : String(error));
      await runFallback(resolvedFrames, `OpenCV analysis failed: ${error?.message ?? String(error)}`);
    }
  };

  const runFallback = async (resolvedFrames, reason) => {
    let fallbackScore = 0;

    try {
      fallbackScore = await computeFallbackDepthScore(resolvedFrames);
    } catch (error) {
      console.error("[Depth] Fallback depth scoring failed:", error);
      setDepthError(error?.message ? String(error.message) : String(error));
    }

    const fallbackConsistency = clamp(fallbackScore / 100, 0, 1);
    const analysisData = {
      baseline: 0,
      frameSimilarity: 0,
      textureScore: 0,
      goodMatchCount: 0,
      disparityMean: 0,
      disparityStdDev: 0,
      homographyInlierRatio: 0,
      motionMean: 0,
      motionStdDev: 0,
      residualMean: 0,
      residualStdDev: 0,
      regionMotionDelta: 0,
      planarityScore: 1,
      depthConsistency: fallbackConsistency,
      mode: "fallback",
    };

    setDepthMode("fallback");
    setDepthScoreValue(fallbackScore);
    setLocalAnalysisData({
      ...analysisData,
      outcome: "fallback",
    });

    if (typeof onAnalysisComplete === "function") {
      const payload = {
        outcome: "fallback",
        confidence: 0,
        reason,
        analysisData,
        depthScore: fallbackScore,
      };

      if (onAnalysisComplete.length >= 2) {
        onAnalysisComplete(fallbackScore, 0);
      } else {
        onAnalysisComplete(payload);
      }
    }
  };

  const processImages = async ([f1, f2], [ori1, ori2]) => {
    const imgA = await processImage(f1);
    const imgB = await processImage(f2);

    try {
      const movementData = calculateMovementData(ori1, ori2);
      const frameSimilarity = compareHistograms(imgA.hist, imgB.hist);
      const textureScore = calculateTextureScore(imgA.gray);

      const featureAnalysis = analyzeFeaturesAndParallax(imgA.gray, imgB.gray);

      const analysisData = {
        frameSimilarity,
        textureScore,
        ...movementData,
        ...featureAnalysis,
      };

      console.log("📊 [Depth] Analysis Data:", analysisData);

      // STEP 11 — Capture Quality Gate
      // Soglia abbassata da (goodMatchCount < 40 || motionMean < 5) a (< minRawMatches || motionMean < 3).
      // Con SWEEP_THRESHOLD_RATIO=0.20 i movimenti più contenuti producono meno match (20-30)
      // e motionMean più basso. La soglia 40 bloccava tutti i window depth come
      // captureQuality=insufficient, impedendo qualsiasi classificazione positiva.
      const captureQuality =
        analysisData.goodMatchCount < DETECTION_PARAMS.minRawMatches ||
        analysisData.motionMean < 3
          ? "insufficient"
          : "good";

      console.log("🧪 [Quality] Capture quality:", captureQuality, {
        goodMatchCount: analysisData.goodMatchCount,
        motionMean: analysisData.motionMean,
        textureScore: analysisData.textureScore,
      });

      const result = evaluateResults(analysisData);

      return {
        ...result,
        captureQuality,
      };

    } finally {
      [imgA.gray, imgA.hist, imgB.gray, imgB.hist].forEach((m) => m?.delete?.());
    }
  };

  const processImage = async (frame) => {
    const img = await loadImage(frame);
    const resizedSource = resizeImageForAnalysis(img, DETECTION_PARAMS.analysisMaxDimension);

    const src = cv.imread(resizedSource);
    const gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

    const hist = new cv.Mat();
    const mask = new cv.Mat();
    const matVector = new cv.MatVector();
    matVector.push_back(gray);
    cv.calcHist(matVector, [0], mask, hist, [256], [0, 256]);

    src.delete();
    mask.delete();
    matVector.delete();

    return { gray, hist };
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const computeFallbackDepthScore = async ([f1, f2]) => {
    const img1 = await loadImage(f1);
    const img2 = await loadImage(f2);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    if (!ctx) {
      throw new Error("Canvas 2D context not available for fallback depth scoring.");
    }

    const w = DETECTION_PARAMS.fallbackCanvasSize;
    const h = DETECTION_PARAMS.fallbackCanvasSize;
    canvas.width = w;
    canvas.height = h;

    ctx.drawImage(img1, 0, 0, w, h);
    const d1 = ctx.getImageData(0, 0, w, h).data;

    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img2, 0, 0, w, h);
    const d2 = ctx.getImageData(0, 0, w, h).data;

    let sum = 0;
    const len = Math.min(d1.length, d2.length);

    for (let i = 0; i < len; i += 4) {
      sum += Math.abs(d1[i] - d2[i]);
      sum += Math.abs(d1[i + 1] - d2[i + 1]);
      sum += Math.abs(d1[i + 2] - d2[i + 2]);
    }

    const denom = w * h * 3 * 255;
    const norm = denom > 0 ? clamp(sum / denom, 0, 1) : 0;

    // Weak fallback: avoid overly high confidence.
    return Math.round(norm * 35);
  };

  const compareHistograms = (hist1, hist2) => {
    return cv.compareHist(hist1, hist2, cv.HISTCMP_CORREL);
  };

  const calculateMovementData = (ori1, ori2) => {
    const baseline = calculate3DMovement(ori1, ori2);
    return { baseline };
  };

  const calculate3DMovement = (ori1, ori2) => {
    if (!ori1 || !ori2) return 0;

    const toRad = (deg) => deg * (Math.PI / 180);
    const dx = toRad((ori2.alpha ?? 0) - (ori1.alpha ?? 0));
    const dy = toRad((ori2.beta ?? 0) - (ori1.beta ?? 0));
    const dz = toRad((ori2.gamma ?? 0) - (ori1.gamma ?? 0));

    const rotationDifference = Math.acos(
      Math.cos(dx) * Math.cos(dy) * Math.cos(dz) +
        Math.sin(dx) * Math.sin(dy) * Math.sin(dz)
    );

    return rotationDifference * (180 / Math.PI);
  };

  const analyzeFeaturesAndParallax = (gray1, gray2) => {
    const detector = new cv.AKAZE();
    const kp1 = new cv.KeyPointVector();
    const kp2 = new cv.KeyPointVector();
    const des1 = new cv.Mat();
    const des2 = new cv.Mat();
    const emptyMask1 = new cv.Mat();
    const emptyMask2 = new cv.Mat();

    try {
      detector.detectAndCompute(gray1, emptyMask1, kp1, des1);
      detector.detectAndCompute(gray2, emptyMask2, kp2, des2);

      if (des1.empty() || des2.empty() || kp1.size() < 4 || kp2.size() < 4) {
        return buildWeakFeatureResult("Insufficient descriptors or keypoints.");
      }

      const rawMatches = matchFeatures(des1, des2);
      const goodMatches = filterGoodMatches(rawMatches);

      if (goodMatches.length < DETECTION_PARAMS.minRawMatches) {
        rawMatches.delete();
        return buildWeakFeatureResult(`Not enough good matches. goodMatches=${goodMatches.length}`);
      }

      const motionData = buildMotionDataFromMatches(kp1, kp2, goodMatches);
      rawMatches.delete();

      if (motionData.count < DETECTION_PARAMS.minRawMatches) {
        return buildWeakFeatureResult(`Motion data insufficient after filtering. count=${motionData.count}`);
      }

      const parallaxData = estimateParallaxFromGlobalMotion(
        motionData.srcPoints,
        motionData.dstPoints,
        gray1.cols,
        gray1.rows
      );

      const depthConsistency = calculateDepthConsistency({
        baseline: 0,
        textureScore: calculateTextureScore(gray1),
        motionMean: motionData.motionMean,
        motionStdDev: motionData.motionStdDev,
        disparityMean: motionData.motionMean,
        disparityStdDev: motionData.motionStdDev,
        residualMean: parallaxData.residualMean,
        residualStdDev: parallaxData.residualStdDev,
        regionMotionDelta: parallaxData.regionMotionDelta,
        homographyInlierRatio: parallaxData.homographyInlierRatio,
        planarityScore: parallaxData.planarityScore,
        goodMatchCount: motionData.count,
      });

      return {
        matchCount: motionData.count,
        goodMatchCount: motionData.count,
        disparityMean: motionData.motionMean,
        disparityStdDev: motionData.motionStdDev,
        motionMean: motionData.motionMean,
        motionStdDev: motionData.motionStdDev,
        residualMean: parallaxData.residualMean,
        residualStdDev: parallaxData.residualStdDev,
        regionMotionDelta: parallaxData.regionMotionDelta,
        homographyInlierRatio: parallaxData.homographyInlierRatio,
        planarityScore: parallaxData.planarityScore,
        depthConsistency,
        mode: "opencv",
      };
    } finally {
      [kp1, kp2, des1, des2, emptyMask1, emptyMask2].forEach((m) => m?.delete?.());
      detector.delete();
    }
  };

  const buildWeakFeatureResult = (reason) => {
    console.warn("[Depth] Weak feature result:", reason);

    return {
      matchCount: 0,
      goodMatchCount: 0,
      disparityMean: 0,
      disparityStdDev: 0,
      motionMean: 0,
      motionStdDev: 0,
      residualMean: 0,
      residualStdDev: 0,
      regionMotionDelta: 0,
      homographyInlierRatio: 1,
      planarityScore: 1,
      depthConsistency: 0,
      weakReason: reason,
      mode: "opencv",
    };
  };

  const matchFeatures = (des1, des2) => {
    const bf = new cv.BFMatcher(cv.NORM_HAMMING, true);
    const matches = new cv.DMatchVector();
    bf.match(des1, des2, matches);
    bf.delete();
    return matches;
  };

  const filterGoodMatches = (matchesVector) => {
    const distances = [];

    for (let i = 0; i < matchesVector.size(); i++) {
      distances.push(matchesVector.get(i).distance);
    }

    if (distances.length === 0) return [];

    const minDistance = Math.min(...distances);
    const dynamicThreshold = Math.max(minDistance * 3, 30);

    const good = [];
    for (let i = 0; i < matchesVector.size(); i++) {
      const match = matchesVector.get(i);
      if (match.distance <= dynamicThreshold) {
        good.push({
          queryIdx: match.queryIdx,
          trainIdx: match.trainIdx,
          distance: match.distance,
        });
      }
    }

    good.sort((a, b) => a.distance - b.distance);
    return good.slice(0, DETECTION_PARAMS.maxGoodMatches);
  };

  const buildMotionDataFromMatches = (kp1, kp2, goodMatches) => {
    const srcPoints = [];
    const dstPoints = [];
    const magnitudes = [];

    for (const match of goodMatches) {
      const pt1 = kp1.get(match.queryIdx).pt;
      const pt2 = kp2.get(match.trainIdx).pt;

      const dx = pt2.x - pt1.x;
      const dy = pt2.y - pt1.y;
      const magnitude = Math.hypot(dx, dy);

      srcPoints.push({ x: pt1.x, y: pt1.y });
      dstPoints.push({ x: pt2.x, y: pt2.y });
      magnitudes.push(magnitude);
    }

    const motionMean = safeMean(magnitudes);
    const motionStdDev = safeStdDev(magnitudes, motionMean);

    return {
      srcPoints,
      dstPoints,
      count: magnitudes.length,
      motionMean,
      motionStdDev,
    };
  };

  const estimateParallaxFromGlobalMotion = (srcPoints, dstPoints, width, height) => {
    if (!srcPoints || !dstPoints || srcPoints.length < 4 || dstPoints.length < 4) {
      return {
        residualMean: 0,
        residualStdDev: 0,
        regionMotionDelta: 0,
        homographyInlierRatio: 1,
        planarityScore: 1,
      };
    }

    let srcMat = null;
    let dstMat = null;
    let homography = null;
    let mask = null;
    let projected = null;

    try {
      const srcFlat = [];
      const dstFlat = [];

      for (let i = 0; i < srcPoints.length; i++) {
        srcFlat.push(srcPoints[i].x, srcPoints[i].y);
        dstFlat.push(dstPoints[i].x, dstPoints[i].y);
      }

      srcMat = cv.matFromArray(srcPoints.length, 1, cv.CV_32FC2, srcFlat);
      dstMat = cv.matFromArray(dstPoints.length, 1, cv.CV_32FC2, dstFlat);
      mask = new cv.Mat();

      homography = cv.findHomography(
        srcMat,
        dstMat,
        cv.RANSAC,
        DETECTION_PARAMS.ransacReprojThreshold,
        mask
      );

      if (!homography || homography.empty()) {
        return estimateParallaxByMedianTranslation(srcPoints, dstPoints, width, height);
      }

      projected = new cv.Mat();
      cv.perspectiveTransform(srcMat, projected, homography);

      const residuals = [];
      const inlierResiduals = [];
      let inliers = 0;

      const projectedData = projected.data32F;
      const maskData = mask.data;

      for (let i = 0; i < srcPoints.length; i++) {
        const px = projectedData[i * 2];
        const py = projectedData[i * 2 + 1];
        const actual = dstPoints[i];
        const residual = Math.hypot(actual.x - px, actual.y - py);

        residuals.push(residual);

        const isInlier = maskData ? maskData[i] !== 0 : true;
        if (isInlier) {
          inliers += 1;
          inlierResiduals.push({
            residual,
            x: srcPoints[i].x,
            y: srcPoints[i].y,
          });
        }
      }

      const residualMean = safeMean(residuals);
      const residualStdDev = safeStdDev(residuals, residualMean);
      const homographyInlierRatio = srcPoints.length > 0 ? inliers / srcPoints.length : 0;
      // STEP 13: use ALL feature residuals (inliers + outliers) for regionMotionDelta.
      // With inliers-only, RANSAC strips out depth-different objects (they become outliers)
      // leaving a flat inlier set — so screens and real scenes looked the same.
      // With all features: real scenes have high-residual outliers concentrated in the center
      // (foreground objects), giving high center-vs-outer delta; screens have uniform
      // random noise from video content, giving near-zero delta.
      const allResidualPoints = srcPoints.map((pt, i) => ({
        residual: residuals[i],
        x: pt.x,
        y: pt.y,
      }));
      const regionMotionDelta = computeRegionDelta(allResidualPoints, width, height);
      const planarityScore = clamp(
        homographyInlierRatio * (1 - normalize01(residualMean, 0.5, 5.0)),
        0,
        1
      );

      return {
        residualMean,
        residualStdDev,
        regionMotionDelta,
        homographyInlierRatio,
        planarityScore,
      };
    } finally {
      [srcMat, dstMat, homography, mask, projected].forEach((m) => m?.delete?.());
    }
  };

  const estimateParallaxByMedianTranslation = (srcPoints, dstPoints, width, height) => {
    const dxs = [];
    const dys = [];

    for (let i = 0; i < srcPoints.length; i++) {
      dxs.push(dstPoints[i].x - srcPoints[i].x);
      dys.push(dstPoints[i].y - srcPoints[i].y);
    }

    const median = (arr) => {
      if (!arr.length) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    };

    const mdx = median(dxs);
    const mdy = median(dys);

    const residuals = srcPoints.map((pt, i) => {
      const predX = pt.x + mdx;
      const predY = pt.y + mdy;
      return {
        residual: Math.hypot(dstPoints[i].x - predX, dstPoints[i].y - predY),
        x: pt.x,
        y: pt.y,
      };
    });

    const values = residuals.map((r) => r.residual);
    const residualMean = safeMean(values);
    const residualStdDev = safeStdDev(values, residualMean);
    const regionMotionDelta = computeRegionDelta(residuals, width, height);

    return {
      residualMean,
      residualStdDev,
      regionMotionDelta,
      homographyInlierRatio: 0,
      planarityScore: clamp(1 - normalize01(residualMean, 0.5, 5.0), 0, 1),
    };
  };

  const computeRegionDelta = (residualPoints, width, height) => {
    if (!residualPoints || residualPoints.length === 0 || !width || !height) return 0;

    const centerXMin = width * 0.33;
    const centerXMax = width * 0.66;
    const centerYMin = height * 0.33;
    const centerYMax = height * 0.66;

    const center = [];
    const outer = [];

    for (const item of residualPoints) {
      const isCenter =
        item.x >= centerXMin &&
        item.x <= centerXMax &&
        item.y >= centerYMin &&
        item.y <= centerYMax;

      if (isCenter) {
        center.push(item.residual);
      } else {
        outer.push(item.residual);
      }
    }

    const centerMean = safeMean(center);
    const outerMean = safeMean(outer);

    return Math.abs(centerMean - outerMean);
  };

  const calculateDepthConsistency = (data) => {
    const motionScore = normalize01(data.motionMean, DETECTION_PARAMS.minMotionMean, 8.0);
    const residualScore = normalize01(data.residualMean, DETECTION_PARAMS.minResidualMean, 6.0);
    const residualSpreadScore = normalize01(data.residualStdDev, DETECTION_PARAMS.minResidualStdDev, 4.0);
    const regionScore = normalize01(data.regionMotionDelta, DETECTION_PARAMS.minRegionMotionDelta, 30.0); // STEP 13: max raised from 4.0 — all-features delta range is now 0–50+
    const antiPlanarityScore = 1 - clamp(data.planarityScore ?? 1, 0, 1);
    const textureScore = clamp(data.textureScore ?? 0, 0, 1);
    const matchScore = normalize01(data.goodMatchCount, 20, 80);

    const depthConsistency = clamp(
      motionScore * 0.18 +
        residualScore * 0.28 +
        residualSpreadScore * 0.18 +
        regionScore * 0.18 +
        antiPlanarityScore * 0.12 +
        textureScore * 0.04 +
        matchScore * 0.02,
      0,
      1
    );

    return depthConsistency;
  };

  const calculateTextureScore = (gray) => {
    const laplacian = new cv.Mat();
    const mean = new cv.Mat();
    const stddev = new cv.Mat();

    try {
      cv.Laplacian(gray, laplacian, cv.CV_64F);
      cv.meanStdDev(laplacian, mean, stddev);

      const sigma = stddev.data64F?.[0] ?? 0;
      const variance = sigma * sigma;
      return clamp(variance / 1000, 0, 1);
    } finally {
      laplacian.delete();
      mean.delete();
      stddev.delete();
    }
  };

  const evaluateResults = (analysisData) => {
    const reasons = [];
    const motionMean = analysisData.motionMean ?? 0;
    const residualMean = analysisData.residualMean ?? 0;
    const regionMotionDelta = analysisData.regionMotionDelta ?? 0;
    const frameSimilarity = analysisData.frameSimilarity ?? 1;
    const homographyInlierRatio = analysisData.homographyInlierRatio ?? 1;
    const screenPlanarCase =
      motionMean > 80 &&
      residualMean > 10 &&
      regionMotionDelta < 0.5 &&
      (frameSimilarity < 0.6 || homographyInlierRatio < 0.2);
    // STEP 10: raised motionMean threshold from 250 to 600.
    // Fast legitimate sweeps (SWEEP_THRESHOLD=0.35) produce motionMean 400-600 with
    // high residualMean from genuine parallax. The old threshold caused false positives.
    const screenDynamicCase =
      motionMean > 600 &&
      residualMean > 150 &&
      homographyInlierRatio < 0.25;
    const suspiciousDynamicScreen =
      screenPlanarCase || screenDynamicCase;

    if ((analysisData.goodMatchCount ?? 0) < DETECTION_PARAMS.minRawMatches) {
      reasons.push("too_few_matches");
    }

    if ((analysisData.homographyInlierRatio ?? 1) > 0.88) {
      reasons.push("high_planarity");
    }

    if ((analysisData.residualMean ?? 0) < 1.2) {
      reasons.push("low_residual_parallax");
    }

    if ((analysisData.regionMotionDelta ?? 0) < 0.8) {
      reasons.push("uniform_motion");
    }
    if (suspiciousDynamicScreen) {
      reasons.push("dynamic_screen_like_motion");
    }

    console.log("🔍 [Depth] Decision metrics:", {
      goodMatchCount: analysisData.goodMatchCount,
      motionMean: analysisData.motionMean,
      motionStdDev: analysisData.motionStdDev,
      residualMean: analysisData.residualMean,
      residualStdDev: analysisData.residualStdDev,
      regionMotionDelta: analysisData.regionMotionDelta,
      homographyInlierRatio: analysisData.homographyInlierRatio,
      planarityScore: analysisData.planarityScore,
      depthConsistency: analysisData.depthConsistency,
      suspiciousDynamicScreen,
      reasons,
    });

    const likelyFlatPlane =
      (analysisData.goodMatchCount ?? 0) < DETECTION_PARAMS.minRawMatches ||
      ((analysisData.homographyInlierRatio ?? 1) > 0.9 &&
        (analysisData.residualMean ?? 0) < 1.5 &&
        (analysisData.regionMotionDelta ?? 0) < 1.0) ||
      suspiciousDynamicScreen ||
      (analysisData.depthConsistency ?? 0) < DETECTION_PARAMS.fakeDepthThreshold;

    if (likelyFlatPlane) {
      return buildResult(
        "Fake depth detected",
        0,
        analysisData,
        "Flat/planar motion detected - possible screen, photo, or insufficient parallax."
      );
    }

    if ((analysisData.depthConsistency ?? 0) < DETECTION_PARAMS.verifyThreshold) {
      return buildResult(
        "Further verification needed",
        analysisData.depthConsistency ?? 0,
        analysisData,
        "Weak parallax signal. Try a wider sweep — move the phone further to each side."
      );
    }

    return buildResult(
      "This is a live scene",
      analysisData.depthConsistency ?? 0,
      analysisData,
      "Live 3D parallax signal detected."
    );
  };

  const buildResult = (outcome, confidence, analysisData, reason = "") => ({
    outcome: outcome.toLowerCase().includes("live scene") ? "this is a live scene" : outcome,
    confidence: Math.round(clamp(confidence, 0, 1) * 100),
    analysisData: {
      ...analysisData,
      parameters: { ...DETECTION_PARAMS },
    },
    reason: reason || "Live scene verified",
  });

  return (
    <div className="analysis-results">
      {localAnalysisData && (
        <div className="result-box">
          {depthScoreValue !== null && (
            <p>
              Depth score{depthMode === "fallback" ? " (fallback)" : ""}: {depthScoreValue}
            </p>
          )}
          {depthError && <p style={{ color: "crimson" }}>error: {depthError}</p>}
          <p>
            DepthConsistency:{" "}
            {(localAnalysisData.depthConsistency ?? 0).toFixed
              ? localAnalysisData.depthConsistency.toFixed(4)
              : String(localAnalysisData.depthConsistency)}
          </p>
          <p>
            Baseline:{" "}
            {(localAnalysisData.baseline ?? 0).toFixed
              ? localAnalysisData.baseline.toFixed(3)
              : String(localAnalysisData.baseline)}
          </p>
          <p>
            Matches: {localAnalysisData.goodMatchCount ?? 0}
          </p>
          <p>
            ResidualMean:{" "}
            {(localAnalysisData.residualMean ?? 0).toFixed
              ? localAnalysisData.residualMean.toFixed(3)
              : String(localAnalysisData.residualMean)}
          </p>
          <p>
            RegionMotionDelta:{" "}
            {(localAnalysisData.regionMotionDelta ?? 0).toFixed
              ? localAnalysisData.regionMotionDelta.toFixed(3)
              : String(localAnalysisData.regionMotionDelta)}
          </p>
          <p>
            HomographyInlierRatio:{" "}
            {(localAnalysisData.homographyInlierRatio ?? 0).toFixed
              ? localAnalysisData.homographyInlierRatio.toFixed(3)
              : String(localAnalysisData.homographyInlierRatio)}
          </p>
        </div>
      )}
    </div>
  );
};

export default AnalyzeDepthFromFrames;
